import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, Link, useParams } from "react-router-dom";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import useLoginCheck from "../util/useLoginCheck";
import Spinner from "../util/Spinner";
import { fetchRestaurantList } from "../slice/restaurantListsSlice";
import {
  fetchRestaurantListItem,
  updateRestaurantListItem,
  deleteRestaurantListItem,
} from "../slice/restaurantListItemsSlice";
import { fetchRatings } from "../slice/ratingSlice";

const RestaurantListItem = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { sid, listsid } = useParams();
  const [shouldRefresh, setShouldRefresh] = useState(true);

  const { restaurantList, isLoading } = useSelector(
    (state) => state.restaurantLists
  );
  const { restaurantListItem, isLoadingItem } = useSelector(
    (state) => state.restaurantListItems
  );

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const reduxRatings = useSelector((state) => state.ratings.ratings);
  const [editableListItem, setEditableListItem] = useState();

  useLoginCheck();

  useEffect(() => {
    if (listsid) {
      dispatch(fetchRestaurantList(listsid));
    }
  }, [dispatch, listsid, shouldRefresh]);

  useEffect(() => {
    if (sid) {
      dispatch(fetchRatings());
      dispatch(fetchRestaurantListItem(sid));
    }
  }, [dispatch, sid]);

  useEffect(() => {
    if (restaurantListItem) {
      setEditableListItem({
        sid: restaurantListItem?.sid,
        dateAttended: restaurantListItem?.dateAttended,
        rating: restaurantListItem?.rating,
        notes: restaurantListItem?.notes,
      });
    }
  }, [restaurantListItem]);

  const deleteHandler = async (e) => {
    e.preventDefault();
    if (window.confirm("Are you sure you want to delete this restaurant?")) {
      try {
        setIsDeleting(true);
        const resultAction = await dispatch(deleteRestaurantListItem(sid));
        if (deleteRestaurantListItem.fulfilled.match(resultAction)) {
          navigate(`/restaurants/${listsid}`);
        }
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsSaving(true);
      const putData = {
        sid: editableListItem?.sid,
        dateAttended: editableListItem?.dateAttended,
        rating: editableListItem?.rating,
        notes: editableListItem?.notes,
      };
      const resultAction = await dispatch(updateRestaurantListItem(putData));
      if (updateRestaurantListItem.fulfilled.match(resultAction)) {
        dispatch(fetchRestaurantListItem(sid));
      }
    } finally {
      setIsSaving(false);
      setIsEditing(false);
    }
  };

  const handleRatingSelected = (e) => {
    const value = parseInt(e.target.value, 10);
    setEditableListItem((prev) => ({ ...prev, rating: value }));
  };

  const setDateAttended = (dateAttended) => {
    setEditableListItem((prev) => ({ ...prev, dateAttended }));
  };

  const handleNotesChange = (e) => {
    const value = e.target.value;
    setEditableListItem((prev) => ({ ...prev, notes: value }));
  };

  const ratingDescription = reduxRatings?.find(
    (r) => r.value === restaurantListItem?.rating
  )?.description;

  const inputClasses =
    "w-full border border-stone-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-600";
  const fieldLabelClasses = "block text-sm font-medium text-stone-500 mb-1";

  return (
    <div className="max-w-2xl mx-auto p-4 md:p-6">
      {isLoading || isLoadingItem ? (
        <Spinner />
      ) : (
        <>
          {/* edit view */}
          {isEditing ? (
            <form
              onSubmit={handleSubmit}
              className="card p-4 md:p-6 space-y-4"
            >
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="btn-primary"
                >
                  {isSaving ? "Saving..." : "Save"}
                </button>
              </div>
              <div>
                <span className={fieldLabelClasses}>Restaurant</span>
                <p className="text-stone-800">
                  {restaurantListItem?.restaurant?.name}
                </p>
              </div>
              <div>
                <label className={fieldLabelClasses}>Date attended</label>
                <Flatpickr
                  className={inputClasses}
                  value={editableListItem?.dateAttended}
                  onChange={(selectedDates) =>
                    setDateAttended(selectedDates[0])
                  }
                  options={{ dateFormat: "Y-m-d" }}
                />
              </div>
              <div>
                <label className={fieldLabelClasses}>Rating</label>
                <select
                  value={editableListItem?.rating ?? -1}
                  onChange={handleRatingSelected}
                  className={inputClasses}
                >
                  <option value="-1" disabled>
                    Rating
                  </option>
                  {reduxRatings.map((r, index) => (
                    <option key={index} value={r.value}>
                      {r.description}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={fieldLabelClasses}>Notes</label>
                <textarea
                  placeholder="Notes"
                  value={editableListItem?.notes ?? ""}
                  onChange={handleNotesChange}
                  className={`${inputClasses} min-h-[100px]`}
                />
              </div>
            </form>
          ) : (
            <>
              {/* detail view */}
              <div className="flex justify-end gap-2 mb-4">
                <button className="btn-primary" onClick={() => setIsEditing(true)}>
                  Edit
                </button>
                <button
                  className="btn-secondary"
                  disabled={isDeleting}
                  onClick={deleteHandler}
                >
                  {isDeleting ? "Deleting..." : "Delete"}
                </button>
              </div>
              <dl className="card p-4 md:p-6 divide-y divide-stone-200">
                <div className="py-3 first:pt-0">
                  <dt className="text-sm font-medium text-stone-500">List</dt>
                  <dd className="mt-1 text-stone-800">
                    <Link
                      to={`/restaurants/${listsid}`}
                      className="text-teal-700 hover:underline"
                    >
                      {restaurantList?.name}
                    </Link>
                  </dd>
                </div>
                <div className="py-3">
                  <dt className="text-sm font-medium text-stone-500">
                    Restaurant
                  </dt>
                  <dd className="mt-1 text-stone-800">
                    {restaurantListItem?.restaurant?.name}
                  </dd>
                </div>
                <div className="py-3">
                  <dt className="text-sm font-medium text-stone-500">
                    Date attended
                  </dt>
                  <dd className="mt-1 text-stone-800">
                    {restaurantListItem?.dateAttended}
                  </dd>
                </div>
                <div className="py-3">
                  <dt className="text-sm font-medium text-stone-500">
                    Rating
                  </dt>
                  <dd className="mt-1 text-stone-800">{ratingDescription}</dd>
                </div>
                <div className="py-3 last:pb-0">
                  <dt className="text-sm font-medium text-stone-500">
                    Notes
                  </dt>
                  <dd className="mt-1 text-stone-800 whitespace-pre-wrap">
                    {restaurantListItem?.notes}
                  </dd>
                </div>
              </dl>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default RestaurantListItem;
